<template>
    <!-- multiple dates selection or direct contact to the hotel -->
    <div class="booking-service-reservation-form">
        <Accordion :items="availableTabs" :value.sync="selectedTab" collapsible>
            <template v-slot:[bookingModes.animodBooking]>
                <div id="tabpanel-variant01" class="avp-tab-panel">

                    <template v-if="!tokenMode">
                        <p v-if="isAnimodBooking" class="card-text">
                            {{
                                $t(
                                    'bookingService.supporting_text.uses_animod_booking_service.intro.text', {
                                        hotel:
                                        activationData.arrangement.hotelname
                                    }
                                )
                            }}
                        </p>
                        <p v-else class="card-text">
                            {{
                                $t('bookingService.supporting_text.no_animod_booking_service.intro.text', {
                                    hotel:
                                    activationData.arrangement.hotelname
                                })
                            }}
                        </p>
                    </template>

                    <div v-else class="row">
                        <div class="col-12 token-mode-notice">
                            <h2 class="h5">{{ $t('bookingService.headline.token_mode.text') }}</h2>
                            <p>{{ $t('bookingService.token_mode.text') }}</p>
                        </div>
                    </div>

                    <form class="form avp-form" role="form" autocomplete="off" @submit.prevent="sendBookingRequest">
                        <template v-if="currentStep === 'step1'">
                            <CheckinDates :checkin-dates.sync="checkinDates"
                                          :duration="duration"
                                          :singleDateSelection="singleDateSelection"
                                          class="avp-date-selection avp-multi-date-selection mb-3"
                            >
                                <h2 slot="before" class="h5">{{ $t('bookingService.date.header') }}</h2>
                            </CheckinDates>

                            <section v-if="!tokenMode" class="avp-card-section">
                                <h2 class="h5" v-if="activationData.mode === bookingModes.animodBooking">{{ $t('bookingService.headline.uses_animod_booking_service.text') }}</h2>
                                <h2 class="h5" v-else>{{ $t('bookingService.headline.no_animod_booking_service.text') }}</h2>
                                <div class="form-row">
                                    <div class="col-12 col-sm-6 form-group">
                                        <label for="avp-firstname">{{ $t('generic.firstname') }}*</label>
                                        <input type="text" name="firstname" id="avp-firstname"
                                               maxlength="80"
                                               v-model="firstname"
                                               class="form-control" required="required"
                                               :placeholder="$t( 'generic.firstname' )"/>
                                    </div>
                                    <div class="col-12 col-sm-6 form-group">
                                        <label for="avp-lastname">{{ $t('generic.lastname') }}*</label>
                                        <input type="text" name="lastname" id="avp-lastname" maxlength="80"
                                               v-model="lastname"
                                               class="form-control" required="required"
                                               :placeholder="$t( 'generic.lastname' )"/>
                                    </div>
                                </div>
                                <div class="form-row">
                                    <div class="col-12 col-sm-6 form-group">
                                        <label
                                            for="avp-email">{{ $t('generic.your_email') }}*</label>
                                        <input type="email" name="emailAddress" id="avp-email"
                                               maxlength="200"
                                               v-model="email"
                                               class="form-control" required="required"
                                               :placeholder="$t( 'generic.email' )"/>
                                    </div>
                                    <div class="col-12 col-sm-6 form-group" v-if="activationData.phoneNumberRequired">
                                        <label for="avp-phone">{{ $t('generic.phone') }}*</label>
                                        <input type="tel" name="phone" id="avp-phone" maxlength="20"
                                               v-model="phone"
                                               class="form-control" required="required"
                                               :placeholder="$t( 'generic.phone' )"/>
                                    </div>
                                    <div class="col-12 col-sm-6 form-group" v-else>
                                        <label for="avp-phone">{{ $t('generic.phone') }}</label>
                                        <input type="tel" name="phone" id="avp-phone" maxlength="20"
                                               v-model="phone"
                                               class="form-control"
                                               :placeholder="$t( 'generic.phone_optional' )"/>
                                    </div>
                                </div>

                                <div class="form-row">
                                    <small class="form-group col-12 text-muted">{{ $t('bookingService.supporting_text.contact_data.annotation.text') }}</small>
                                </div>
                            </section>

                            <section class="avp-card-section">
                                <h2 class="h5">{{ $t('bookingService.headline.additional_information.text') }}</h2>

                                <div v-if="activationData.bookingServiceNotice"
                                     v-html="activationData.bookingServiceNotice">
                                </div>

                                <div class="form-group">
                                        <textarea name="additionalInformation" v-model="computedComment"
                                                  class="form-control maxlength"
                                                  id="comment" :maxlength="activationData.maximumSizeForAdditionalInformation" rows="5"
                                                  :placeholder=additionalInformationPlaceHolderText>
                                        </textarea>
                                    <small class="comment-counter text-secondary">
                                        <label class="mb-0">
                                            {{ $t('bookingService.supporting_text.additional_information.annotation.text') }}
                                        </label>
                                        <span class="char-counter">{{ remainingCommentChars }}</span>
                                    </small>
                                </div>

                                <div class="form-row" v-if=isNewsletterSignupVisible>
                                    <div class="form-group form-check">
                                        <input type="checkbox" class="form-check-input" v-model="newsletterAccepted" id="bookingNewsletterCheck" />
                                        <i18n path="newsletter.optin_text" class="form-check-label" tag="label" for="bookingNewsletterCheck"/>
                                    </div>
                                </div>

                                <div class="d-flex justify-content-between">
                                    <button class="btn btn-outline-secondary"
                                            type="button"
                                            @click="$store.dispatch('reset')">
                                        {{ $t('generic.reset') }}
                                    </button>

                                    <button v-if="travellerDataRequired" type="submit" class="btn btn-primary">
                                        {{ $t('generic.next') }}
                                    </button>

                                    <button v-else type="submit" class="btn btn-primary">
                                        <span class="d-lg-none">{{ $t('generic.send') }}</span>
                                        <span class="d-none d-lg-block">{{ $t('generic.submit_now') }}</span>
                                    </button>
                                </div>
                            </section>
                        </template>
                        <template v-if="currentStep === 'step2'">
                            <div v-if="activationData.travellerDataMode === 'PersonCount'" class="form-row">
                                <div class="col-12">
                                    <section class="avp-card-section">
                                        <h2 class="h5">
                                            {{ $t('bookingService.headline.step2.PersonCount.text') }}</h2>
                                        <div class="form-row">
                                            <div class="form-group col-12 col-sm-6">
                                                <label for="numberOfAdults">{{$t('bookingService.form.traveller_data.numberOfAdults.label')}}*</label>
                                                <select v-model="numberOfAdults" name="numberOfAdults"
                                                        id="numberOfAdults"
                                                        class="form-control">
                                                    <option v-for="option in 4" :key="'adult' + option" :value="option">
                                                        {{ option }}
                                                    </option>
                                                    <!--<option value="1">1</option>
                                                    <option value="2">2</option>
                                                    <option value="3">3</option>
                                                    <option value="4">4</option>-->
                                                </select>
                                            </div>
                                            <div class="form-group col-12 col-sm-6">
                                                <label for="numberOfChildren">{{ $t('bookingService.form.traveller_data.numberOfChildren.label') }}*</label>
                                                <select v-model="numberOfChildren" name="numberOfChildren"
                                                        id="numberOfChildren" class="form-control">
                                                    <option v-for="option in 10" :key="'child' + option"
                                                            :value="option - 1"
                                                    >
                                                        {{ option - 1 }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>

                                        <template v-if="numberOfChildren > 0">
                                            <h2 class="h5">
                                                {{ $t('bookingService.headline.step2.PersonCount.text2') }}</h2>
                                            <div class="form-row">
                                                <div v-for="(infant, index) in infants"
                                                     class="form-group col-xs-6 col-sm-4"
                                                     :key="'infant' + index">
                                                    <label :for="'infant' + index">{{ $t('bookingService.form.traveller_data.numberOfChildren.child') }} {{ index + 1 }}</label>
                                                    <select v-model="infant.age" class="form-control input-gray"
                                                            :id="'infant' + index" required>
                                                        <option v-for="age in 17"
                                                                :value="age"
                                                                :key="'ageOption' + age"
                                                        >
                                                            {{ age }} {{ $tc('generic.year', age) }}
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>
                                        </template>

                                        <div class="form-row">
                                            <small class="form-group col-12 text-muted">{{ $t('bookingService.supporting_text.contact_data.annotation.text') }}</small>
                                        </div>
                                    </section>
                                </div>
                            </div>
                            <div v-if="activationData.travellerDataMode === 'FullDetails'">
                                <section class="avp-card-section">
                                    <h2 class="h5">
                                        {{ $t('bookingService.headline.step2.FullDetails.text') }}
                                    </h2>

                                    <div v-for="(person, index) in persons" :key="'person' + index"
                                         class="form-row">
                                        <div class="form-group col-6 col-sm">
                                            <input type="text" v-model="person.firstname"
                                                   class="form-control input-gray" required
                                                   :placeholder="$t( 'generic.firstname' ) + '*'"
                                                   maxlength="30"
                                            />
                                        </div>
                                        <div class="form-group col-6 col-sm">
                                            <input type="text" v-model="person.lastname"
                                                   class="form-control input-gray" required
                                                   :placeholder="$t( 'generic.lastname' ) + '*'"
                                                   maxlength="30"
                                            />
                                        </div>
                                        <div class="form-group col-6 col-sm-3">
											<DateInput v-model="person.birthdate" class="form-control input-gray" :placeholder="$t('bookingService.form.contact_data.birthdate.placeholder') + '*'"
													   :incorrectDateMessage="$t('bookingService.form.contact_data.birthdate.incorrectDate')"
													   required
													   :tooltip="'Format: ' + $t('bookingService.form.contact_data.birthdate.placeholder')" />
                                        </div>
                                    </div>

                                    <div class="form-row">
                                        <small class="form-group col-12 text-muted">{{ $t('bookingService.supporting_text.contact_data.annotation.text') }}</small>
                                    </div>

                                </section>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <section class="avp-card-section">
                                        <div class="d-flex justify-content-between">
                                            <button type="button" class="btn btn-outline-secondary"
                                                    @click="currentStep='step1'">
                                                {{ $t('generic.back') }}
                                            </button>
                                            <button type="submit" class="btn btn-primary">
                                                {{ $t('generic.submit_now') }}
                                            </button>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </template>
                    </form>
                </div>
            </template>

            <template v-slot:[bookingModes.hotelBooking]>
                <div id="tabpanel-variant02" class="avp-tab-panel"
                     role="tabpanel">
                    <i18n path="bookingService.supporting_text.reservation_variant_02.intro.text" tag="p">
                        <mark>{{ voucherCode }}</mark>
                    </i18n>
                    <h3 class="h5">{{ activationData.hotelContactData.name }}</h3>
                    <div class="row">
                        <div class="col-12 col-md-6">
                            <table class="table table-sm">
                                <tr>
                                    <th>
                                        {{ $t('generic.phone') }}:
                                    </th>
                                    <td>{{ activationData.hotelContactData.phone }}</td>
                                </tr>
                                <tr>
                                    <th>
                                        {{ $t('bookingService.supporting_text.reservation_variant_02.contact_data.fax.label') }}:
                                    </th>
                                    <td>{{ activationData.hotelContactData.fax }}</td>
                                </tr>
                                <tr>
                                    <th>
                                        {{ $t('generic.email') }}:
                                    </th>
                                    <td class="email-cell">
                                        {{ activationData.hotelContactData.email }}
                                    </td>
                                </tr>
                            </table>
                        </div>
                        <div class="col-12 col-md-6">
                            <h4 class="h5">{{ $t('bookingService.supporting_text.reservation_variant_02.contact_data.address.headline.text') }}</h4>
                            <p class="card-text">
                                {{ activationData.hotelContactData.street }}<br>
                                {{ activationData.hotelContactData.zipcode }}
                                {{ activationData.hotelContactData.city }}<br>
                                {{ activationData.hotelContactData.country }}
                            </p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 mb-4">
                            <p class="card-text">
                                {{ $t('bookingService.supporting_text.reservation_variant_02.outro.text') }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </Accordion>
    </div>
    <!-- /v-else -->
</template>

<script>
import CustomerServiceEmail from './CustomerServiceEmail'
import CustomerServicePhone from './CustomerServicePhone'
import ErrorMessages from '../components/ErrorMessages'
import GdprNotice from '../components/GdprNotice'
import Accordion from '@/components/Accordion'
import Tab from '@/models/Tab'
import CheckinDates from '@/components/CheckinDates'
import bookingModes from '@/models/bookingModes'
import { mapGetters, mapState } from 'vuex'
import ResponseError from '@/models/ResponseError';
import DateInput from 'base/components/form/DateInput';

//const nonUniqueDatesError = "Bitte wählen Sie drei unterschiedliche Wunschtermine aus.";

export default {
    name: 'BookingServiceReservationForm',
    components: {
        CustomerServiceEmail,
        CustomerServicePhone,
        ErrorMessages,
        GdprNotice,
        Accordion,
        CheckinDates,
		DateInput
    },
    data () {
        const tabs = [
            new Tab({
                header: this.$i18n.t('bookingService.headline.reservation_variant_01.text'),
                value: bookingModes.animodBooking
            }),
            new Tab({
                header: this.$i18n.t('bookingService.headline.reservation_variant_02.text'),
                value: bookingModes.hotelBooking
            })
        ]

        return {
            tabs,
            bookingModes,
            selectedTab: null,
            currentStep: 'step1',
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            newsletterAccepted: false,
            comment: '',
            checkinDates: [],
            errors: [],
            reservationForm: null,
            numberOfChildren: 0,
            infants: [],
            persons: []
        }
    },
    props: {
        reservationMode: String
    },
    computed: {
        ...mapState(['voucher', 'activationData']),
        ...mapGetters(['isAnimodBooking', 'isHotelBooking', 'lang', 'singleDateSelection']),
        computedComment: {
            get () {
                return this.activationData.additionalInformation || this.comment
            },
            set (value) {
                this.comment = value
            }
        },
        availableTabs () {
            if (!this.isHotelContactDataPresent) {
                return this.tabs.filter(tab => tab.value !== this.bookingModes.hotelBooking)
            }
            return this.tabs
        },
        tokenMode () {
            return this.activationData.token !== null
        },
        voucherCode () {
            return !this.tokenMode && this.voucher && this.voucher.voucherCode ? this.voucher.voucherCode : null
        },
        duration() {
            return this.activationData.arrangement.productVariant.numberOfNights;
        },
        numberOfAdults: {
            get () {
                if (this.activationData.travellerDataMode === 'PersonCount'
                    || this.activationData.travellerDataMode === 'FullDetails') {
                    return this.activationData.arrangement.productVariant.numberOfPersons
                }
            },
            set (value) {
                //forgive me Evan You. Committing a store mutation is triple the code
                this.activationData.arrangement.productVariant.numberOfPersons = value
            }
        },
        travellerData () {
            return [
                ...this.persons.map(adult => ({
                    firstname: adult.firstname || '-',
                    lastname: adult.lastname || '-',
                    salutation: adult.salutation || null,
                    birthdate: adult.birthdate || null,
                    child: false
                })),
                ...this.infants.map(infant => ({
                    firstname: '-',
                    lastname: '-',
                    birthdate: '01.01.' + (new Date().getFullYear() - +infant.age),
                    child: true
                }))
            ]
        },
        remainingCommentChars () {
            const numberOfMaxCommentChars = 2000
            return numberOfMaxCommentChars - (this.comment && this.comment.length ? this.comment.length : 0)
        },
        additionalInformationPlaceHolderText () {
            if (this.activationData.mode === this.bookingModes.animodBooking) {
                return this.$i18n.t('bookingService.form.additional_information.use_animod_booking_service.placeholder')
            }
            return this.$i18n.t('bookingService.form.additional_information.no_animod_booking_service.placeholder')
        },
        productVariant () {
            return this.activationData.arrangement.productVariant || {}
        },
        travellerDataRequired () {
            return this.activationData.travellerDataMode !== 'NoData'
        },
        isHotelContactDataPresent () {
            return !this.tokenMode
                && this.activationData.hotelContactData
                && (this.activationData.hotelContactData.phone || this.activationData.hotelContactData.email)
        },
        isNewsletterSignupVisible() {
            let lang = ('' + this.$store.state.locale) || 'de';
            if (lang.length > 2) {
                lang = lang.substring(0, 2);
            }
            let isCzechInstance = location.hostname === 'voucher.animod.cz' || location.search.indexOf('czech=true') !== -1;
            console.log("isNewsletterSignupVisible::isCzechInstance", isCzechInstance);
            console.log("isNewsletterSignupVisible::lang", lang);
            console.log("isNewsletterSignupVisible", (lang === 'de' && !isCzechInstance));
            return lang === 'de' && !isCzechInstance;
        }
    },
    methods: {
        async sendBookingRequest () {
            console.log('sendBookingRequest')
            this.$store.commit('setErrors', [])

            const checkinDates = this.checkinDates.filter(Boolean)

            if (!checkinDates.length) {
                this.$store.commit('setErrors', [
                    'bookingService.errors.send_booking_request.singledate_selection.error.text'
                ])
                return
            }

            if (this.currentStep === 'step1' && !this.tokenMode && this.travellerDataRequired) {
                this.currentStep = 'step2'
            } else {
                const {
                    voucherCode, firstname, lastname, email, phone, newsletterAccepted, isNewsletterSignupVisible, travellerData,
                    comment: additionalInformation, lang: language, activationData
                } = this
                console.log("nl-flag-status", isNewsletterSignupVisible, newsletterAccepted);

                //sometimes errors are returned as 200
                const { successful, errorMessage } = await this.$store.dispatch('sendBookingRequest', {
                    voucherCodes: [voucherCode],
                    token: activationData.token,
                    firstname, lastname, email, phone,
                    newsletterAccepted : (isNewsletterSignupVisible && newsletterAccepted),
                    additionalInformation, checkinDates, travellerData,
                    language
                })

                if (successful) {
                    this.$store.commit('setSuccess', true);
                }
                else {
                    const error = new ResponseError("Not successful", {
                        status: 400,
                        translationKey: (errorMessage && errorMessage.includes("has no vacancy"))
                            ? "bookingService.errors.noVacancy" : "errors.requestFailed"
                    });
                    this.$store.dispatch("processError", error);
                }
            }
        }
    },
    watch: {
        reservationMode: {
            handler (mode) {
                this.selectedTab = mode
            },
            immediate: true
        },
        numberOfAdults (val) {
            while (this.persons.length < val) {
                this.persons.push({ age: 1 })
            }
            this.persons = this.persons.slice(0, val)
        },
        numberOfChildren (val) {
            while (this.infants.length < val) {
                this.infants.push({ age: 1 })
            }
            this.infants = this.infants.slice(0, val)
        }
    },
    mounted () {
        this.persons = []
        for (let i = 0; i < this.numberOfAdults; i++) {
            this.persons.push({
                salutation: null,
                firstname: '',
                lastname: '',
                birthdate: null
            })
        }
    }
}
</script>

<style lang="scss">

.booking-service-reservation-form > *:last-child {
    margin-bottom: 0 !important;
}

.token-mode-notice {
    border: 1px dashed grey;
    padding-top: 20px;
}

.avp-form {
    .avp-card-section {
        margin-bottom: 1.25rem;

        &:last-child {
            margin-bottom: 0;
        }

        .avp-form-button-row {
            justify-content: space-between;

            @media (max-width: 575px) {
                flex-wrap: wrap-reverse;
            }
        }
    }


    .char-counter {
        display: inline-block;
        margin-left: 0.625rem;
    }
}

mark {
    display: inline-block;
    color: hsl(0, 0%, 0%);
    font-size: 18px;
    padding: 0 0.3125rem;
    font-family: "Courier New", monospace, serif;
    font-weight: bold;
}

.email-cell {
    max-width: 190px;
}
</style>
